import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useWalletObserver } from "@sundaeswap/wallet-lite";

export type NetworkMismatch =
  | null                // no mismatch (or no wallet connected)
  | "mainnet-wallet"    // app is testnet but wallet is mainnet
  | "wrong-testnet";   // app is preview but wallet appears to be on preprod

export function useNetworkCheck(): {
  mismatch: NetworkMismatch;
  isChecking: boolean;
} {
  const { mainAddress } = useWalletObserver();

  const appEnv = window.__APP_CONFIG.env;
  const isAppMainnet = appEnv === "mainnet";

  // Step 1: check address prefix
  const prefixMismatch = useMemo<NetworkMismatch>(() => {
    if (!mainAddress) return null;
    const isMainnetAddress = mainAddress.startsWith("addr1");
    if (isAppMainnet && !isMainnetAddress) return "wrong-testnet";
    if (!isAppMainnet && isMainnetAddress) return "mainnet-wallet";
    return null;
  }, [mainAddress, isAppMainnet]);

  // Step 2: if on testnet and address looks like testnet, verify UTxOs exist
  // on the app's network via blockfrost. If they don't, likely wrong testnet.
  const shouldVerify = !!mainAddress && !prefixMismatch && !isAppMainnet;

  const { data: utxoCheck, isLoading } = useQuery({
    queryKey: ["network-check", mainAddress],
    enabled: shouldVerify,
    staleTime: Infinity,
    retry: 1,
    queryFn: async () => {
      const network = "preview";
      const res = await fetch(
        `https://cardano-${network}.blockfrost.io/api/v0/addresses/${mainAddress}/utxos?count=1`,
        {
          headers: {
            project_id: window.__APP_CONFIG.blockfrostProjectId,
          },
        },
      );

      if (res.status === 404) return "not-found" as const;
      if (!res.ok) return "error" as const;

      const data = await res.json();
      return Array.isArray(data) && data.length > 0
        ? ("found" as const)
        : ("empty" as const);
    },
  });

  const mismatch = useMemo<NetworkMismatch>(() => {
    if (prefixMismatch) return prefixMismatch;
    if (utxoCheck === "not-found") return "wrong-testnet";
    return null;
  }, [prefixMismatch, utxoCheck]);

  return {
    mismatch,
    isChecking: shouldVerify && isLoading,
  };
}
